import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useFormWithValidation, useProjectSiteParams } from '@/shared/lib';
import {
  useGetConsignmentByIdQuery,
  useUpdateConsignment,
  type ConsignmentUpdateRequest
} from '@/entities/consignments';

import { createConsignmentSchema, type ConsignmentFormValues } from './validation';

interface UseConsignmentFormProps {
  consignmentId: string;
  onSave: (data: unknown) => void;
}

export const useConsignmentForm = ({ consignmentId, onSave }: UseConsignmentFormProps) => {
  const { t } = useTranslation();
  const { projectId, siteId } = useProjectSiteParams();

  const { data: consignmentData, isFetching: isFetchingConsignment } = useGetConsignmentByIdQuery(
    {
      id: consignmentId,
      projectId,
      siteId
    },
    {
      skip: !consignmentId || !projectId || !siteId
    }
  );

  const consignmentSchema = createConsignmentSchema(t);
  const form = useFormWithValidation(consignmentSchema, {
    status: 'new',
    priority: 'normal',
    plannedMode: 'none'
  });

  const { updateConsignment, isLoading: isUpdating } = useUpdateConsignment({
    onSuccess: (data) => {
      toast.success(t('consignment.edit.saveSuccess', 'Consignment saved successfully'));
      onSave(data);
    },
    onError: (error) => {
      toast.error(t('consignment.edit.saveError', 'Failed to save consignment'));
      console.error('Failed to save consignment:', error);
    }
  });

  const onSubmit = useCallback(
    async (data: ConsignmentFormValues) => {
      if (!projectId || !siteId) {
        toast.error(t('consignment.edit.missingParams', 'Missing project or site parameters'));
        return;
      }

      // Map form data to API request format
      const updateRequest: ConsignmentUpdateRequest = {
        ownersRef: data.ownersReference,
        remarks: data.remarks,
        senderContactId: data.senderContact,
        receiverContactId: data.receiverContact,
        invoiceContactId: data.clearingAgentContact,
        // Add date fields if they exist
        ...(data.expectedArrivalDate && { loadDate: new Date(data.expectedArrivalDate).getTime() }),
        ...(data.onSiteDate && { dispatchPlanDate: new Date(data.onSiteDate).getTime() })
        // TODO: Map other fields when API integration is complete
        // arrivalLocationId: data.arrivingTo
        // destinationLocationId: based on servicePoint selection
        // Additional fields from starting point section
      };

      try {
        await updateConsignment(
          {
            id: consignmentId,
            projectId: projectId as number,
            siteId: siteId as number
          },
          updateRequest
        );
      } catch (error) {
        // Error handled in useUpdateConsignment hook
      }
    },
    [consignmentId, projectId, siteId, updateConsignment, t]
  );

  //TODO: Rewrite this effect to fetch valid data for the form
  useEffect(() => {
    if (consignmentData) {
      // Map API response to form values using only available fields from ConsignmentSummary
      const formDefaults: Partial<ConsignmentFormValues> = {
        ownersReference: consignmentData.ownersRef || '',
        remarks: consignmentData.remarks || '',
        status: consignmentData.statusCode === 'UR' ? 'urgent' : consignmentData.statusCode === 'NR' ? 'normal' : 'new',
        priority:
          consignmentData.dispatchPriorityName?.toLowerCase() === 'urgent'
            ? 'urgent'
            : consignmentData.dispatchPriorityName?.toLowerCase() === 'high'
              ? 'high'
              : 'normal',
        plannedMode: mapTransportModeToPlannedMode(consignmentData.transportModeDescription),
        transportUnitQuantity: consignmentData.numberOfPallets,
        senderOrganization: consignmentData.senderContact?.organization?.id
          ? String(consignmentData.senderContact.organization.id)
          : undefined,
        senderContact: consignmentData.senderContact?.contact?.id
          ? String(consignmentData.senderContact.contact.id)
          : undefined,
        receiverOrganization: consignmentData.receiverContact?.organization?.id
          ? String(consignmentData.receiverContact.organization.id)
          : undefined,
        receiverContact: consignmentData.receiverContact?.contact?.id
          ? String(consignmentData.receiverContact.contact.id)
          : undefined,
        clearingAgent: consignmentData.clearingAgent?.organization?.id
          ? String(consignmentData.clearingAgent.organization.id)
          : undefined,
        clearingAgentContact: consignmentData.clearingAgent?.contact?.id
          ? String(consignmentData.clearingAgent.contact.id)
          : undefined,
        // Map dates if they exist
        ...(consignmentData.estimatedDispatchTime && {
          onSiteDate: new Date(consignmentData.estimatedDispatchTime).toISOString()
        })
        // Note: Other fields like documentNo, contactInfo, instructions, serviceInstructions,
        // missionTransporterNumber, date, startingPoint, and servicePoint are not available
        // in ConsignmentSummary and would need to be fetched from a detailed endpoint
      };

      form.reset(formDefaults);
    }
  }, [consignmentData, form]);

  // Helper function to map transport mode to planned mode
  const mapTransportModeToPlannedMode = (transportMode?: string): 'none' | 'air' | 'road' | 'sea' | 'rail' => {
    if (!transportMode) return 'none';
    const mode = transportMode.toLowerCase();
    if (mode.includes('air')) return 'air';
    if (mode.includes('road')) return 'road';
    if (mode.includes('sea')) return 'sea';
    if (mode.includes('rail')) return 'rail';
    return 'none';
  };

  return {
    form,
    onSubmit,
    isUpdating,
    isFetchingConsignment,
    consignmentData
  };
};
